import { Injectable } from '@angular/core';

/**
 * Generic Filter State Service
 * 
 * This service provides a reusable solution for persisting filter states across the application.
 * It uses sessionStorage to maintain filter states during a user's session and provides
 * mechanisms to automatically clear filters when navigating outside specific modules.
 * 
 * Usage:
 * 1. Inject this service into your component
 * 2. Call saveFilterState() whenever filters change
 * 3. Call getFilterState() in ngOnInit to restore filters
 * 4. Call clearFilterState() to reset filters
 * 
 * The service automatically handles:
 * - Serialization/deserialization of filter objects
 * - Module-specific storage keys
 * - Cleanup on module navigation
 */
@Injectable({
  providedIn: 'root'
})
export class FilterStateService {
  private readonly FILTER_KEY_PREFIX = 'filter_state_';
  private readonly PAGINATION_KEY_SUFFIX = '_pagination';
  private readonly SORTING_KEY_SUFFIX = '_sorting';
  private readonly CURRENT_MODULE_KEY = 'current_filter_module';

  constructor() { }

  /**
   * Save filter state to sessionStorage
   * @param moduleKey Unique identifier for the module (e.g., 'followup_deviations')
   * @param filterState The filter state object to save
   */
  saveFilterState<T>(moduleKey: string, filterState: T): void {
    try {
      const key = this.getStorageKey(moduleKey);
      const serializedState = JSON.stringify(filterState);
      sessionStorage.setItem(key, serializedState);
      
      // Track current module for cleanup
      sessionStorage.setItem(this.CURRENT_MODULE_KEY, moduleKey);
    } catch (error) {
      console.error('Error saving filter state:', error);
    }
  }

  /**
   * Retrieve filter state from sessionStorage
   * @param moduleKey Unique identifier for the module
   * @returns The filter state object or null if not found
   */
  getFilterState<T>(moduleKey: string): T | null {
    try {
      const key = this.getStorageKey(moduleKey);
      const serializedState = sessionStorage.getItem(key);
      
      if (serializedState) {
        return JSON.parse(serializedState) as T;
      }
      
      return null;
    } catch (error) {
      console.error('Error retrieving filter state:', error);
      return null;
    }
  }

  /**
   * Clear filter state for a specific module
   * @param moduleKey Unique identifier for the module
   */
  clearFilterState(moduleKey: string): void {
    try {
      const filterKey = this.getStorageKey(moduleKey);
      const paginationKey = this.getStorageKey(moduleKey + this.PAGINATION_KEY_SUFFIX);
      const sortingKey = this.getStorageKey(moduleKey + this.SORTING_KEY_SUFFIX);
      
      sessionStorage.removeItem(filterKey);
      sessionStorage.removeItem(paginationKey);
      sessionStorage.removeItem(sortingKey);
      
      // Clear current module tracking if it matches
      const currentModule = sessionStorage.getItem(this.CURRENT_MODULE_KEY);
      if (currentModule === moduleKey) {
        sessionStorage.removeItem(this.CURRENT_MODULE_KEY);
      }
    } catch (error) {
      console.error('Error clearing filter state:', error);
    }
  }

  /**
   * Save pagination state
   * @param moduleKey Unique identifier for the module
   * @param paginationState The pagination state to save
   */
  savePaginationState<T>(moduleKey: string, paginationState: T): void {
    try {
      const key = this.getStorageKey(moduleKey + this.PAGINATION_KEY_SUFFIX);
      const serializedState = JSON.stringify(paginationState);
      sessionStorage.setItem(key, serializedState);
    } catch (error) {
      console.error('Error saving pagination state:', error);
    }
  }

  /**
   * Retrieve pagination state
   * @param moduleKey Unique identifier for the module
   * @returns The pagination state or null if not found
   */
  getPaginationState<T>(moduleKey: string): T | null {
    try {
      const key = this.getStorageKey(moduleKey + this.PAGINATION_KEY_SUFFIX);
      const serializedState = sessionStorage.getItem(key);
      
      if (serializedState) {
        return JSON.parse(serializedState) as T;
      }
      
      return null;
    } catch (error) {
      console.error('Error retrieving pagination state:', error);
      return null;
    }
  }

  /**
   * Save sorting state
   * @param moduleKey Unique identifier for the module
   * @param sortingState The sorting state to save
   */
  saveSortingState<T>(moduleKey: string, sortingState: T): void {
    try {
      const key = this.getStorageKey(moduleKey + this.SORTING_KEY_SUFFIX);
      const serializedState = JSON.stringify(sortingState);
      sessionStorage.setItem(key, serializedState);
    } catch (error) {
      console.error('Error saving sorting state:', error);
    }
  }

  /**
   * Retrieve sorting state
   * @param moduleKey Unique identifier for the module
   * @returns The sorting state or null if not found
   */
  getSortingState<T>(moduleKey: string): T | null {
    try {
      const key = this.getStorageKey(moduleKey + this.SORTING_KEY_SUFFIX);
      const serializedState = sessionStorage.getItem(key);
      
      if (serializedState) {
        return JSON.parse(serializedState) as T;
      }
      
      return null;
    } catch (error) {
      console.error('Error retrieving sorting state:', error);
      return null;
    }
  }

  /**
   * Check if a module has saved filter state
   * @param moduleKey Unique identifier for the module
   * @returns true if filter state exists
   */
  hasFilterState(moduleKey: string): boolean {
    const key = this.getStorageKey(moduleKey);
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Clear all filter states for a specific module prefix
   * Useful for clearing all states when leaving a parent module
   * @param modulePrefix The module prefix (e.g., 'followup_' will clear all followup modules)
   */
  clearModuleFilterStates(modulePrefix: string): void {
    try {
      const keys = Object.keys(sessionStorage);
      const filterPrefix = this.getStorageKey(modulePrefix);
      
      keys.forEach(key => {
        if (key.startsWith(filterPrefix)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing module filter states:', error);
    }
  }

  /**
   * Get the current module that has active filters
   * @returns The current module key or null
   */
  getCurrentModule(): string | null {
    return sessionStorage.getItem(this.CURRENT_MODULE_KEY);
  }

  /**
   * Clear filter state when navigating outside a specific module
   * @param newModuleKey The module being navigated to
   * @param currentModulePrefix The current module prefix (e.g., 'followup')
   */
  clearOnModuleNavigation(newModuleKey: string, currentModulePrefix: string): void {
    const currentModule = this.getCurrentModule();
    
    // If current module starts with the prefix and new module doesn't, clear filters
    if (currentModule && 
        currentModule.startsWith(currentModulePrefix) && 
        !newModuleKey.startsWith(currentModulePrefix)) {
      this.clearModuleFilterStates(currentModulePrefix);
    }
  }

  /**
   * Generate storage key with prefix
   * @param moduleKey The module key
   * @returns The full storage key
   */
  private getStorageKey(moduleKey: string): string {
    return `${this.FILTER_KEY_PREFIX}${moduleKey}`;
  }

  /**
   * Clear old/legacy filter states
   * This method can be used to clean up old filter storage formats
   * @param oldKeys Array of old storage keys to remove
   */
  clearLegacyFilterStates(oldKeys: string[]): void {
    try {
      oldKeys.forEach(key => {
        sessionStorage.removeItem(key);
      });
    } catch (error) {
      console.error('Error clearing legacy filter states:', error);
    }
  }
}

